import React from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import {
  Game,
  CreateGameRequest,
  UpdateGameRequest,
} from "../../api/services/games/type";
import PrimaryButton from "../ui/PrimaryButton";
import { ReactComponent as CoinIcon } from "../../assets/ic_coin.svg";
import CustomInput from "../ui/CustomInput";

interface GameFormProps {
  game?: Game;
  isLoading?: boolean;
  onSubmit: (data: CreateGameRequest | UpdateGameRequest) => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
}

export const GameForm: React.FC<GameFormProps> = ({
  game,
  isLoading = false,
  onSubmit,
  onCancel,
}) => {
  const isEditMode = !!game;
  const formTitle = isEditMode ? "Edit Game" : "Create New Game";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: game?.name || "",
    },
  });

  const handleFormSubmit = (data: FormData) => {
    const payload = { ...data, name: data.name.trim() };
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">{formTitle}</h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <CustomInput
            label="Game Name"
            placeholder="Enter game name"
            type="text"
            name="name"
            register={register}
            validation={{
              required: "Game name is required",
              validate: (v: string) => {
                const len = v.trim().length;
                if (len === 0) return "Game name is required";
                if (len < 2) return "Game name must be at least 3 characters";
                if (len > 50)
                  return "Game name must be less than 50 characters";
              },
            }}
            error={errors.name}
            disabled={isLoading}
            icon={<CoinIcon className="w-5 h-5 text-slate-400" />}
            autoFocus
          />

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <PrimaryButton type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : isEditMode ? "Update" : "Create"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};
